import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import SearchIcon from "@mui/icons-material/Search";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Checkbox, InputBase, Slider, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import AlphaSort from "@/components/Icons/AlphaSort";
import AlphaSortDesc from "@/components/Icons/AlphaSortDesc";
import RecentSort from "@/components/Icons/RecentSort";
import { ampli } from "@/config";
import { uniqueTagValuesForVisibleDrivers } from "@/hooks/driver/tagFilterOptions";
import { type ColumnSort, GROUP_SORT_COLUMN_KEY } from "@/hooks/driver/tagFilters";
import type { useDriverFilters } from "@/hooks/driver/useDriverFilters";
import { useLocationFilterForm } from "@/hooks/driver/useLocationFilterForm";
import {
  DRIVER_DUTY_STATUS_FILTERS,
  type DriverDutyStatusFilter,
  type DriverSortKey,
  type DriverStatusFilter,
  type DriverVolumeFilter,
  type LocationFilter,
  type TagDefinition,
} from "@/types/driver";
import FilterListItem from "@/ui/FilterListItem";
import FilterSectionHeader from "@/ui/FilterSectionHeader";
import SegmentedControl from "@/ui/SegmentedControl";
import { zinc } from "@/ui/tokens";
import { formatDutyStatusLabel } from "./columns/dutyStatusFormat";
import { HANDS_FREE_DISPLAYS, NEVER_LOGGED_IN_DISPLAY, OFFLINE_DISPLAY } from "./columns/handsFreeAvailability";
import HeaderPopover from "./HeaderPopover";

type GroupOption = { id: string; name: string; active: boolean };

export type TagFilterState = {
  tagFilters: Record<string, string[]>;
  toggleTagFilterValue: (tagId: string, value: string) => void;
  selectTagFilterValues: (tagId: string, values: string[]) => void;
  clearTagFilter: (tagId: string) => void;
  clearAllTagFilters: () => void;
  hasActiveTagFilters: boolean;
  columnSort: ColumnSort | null;
  setColumnSort: (sort: ColumnSort | null) => void;
};

export function useColumnFilters({
  filters,
  groups,
  tagValueMap,
  visibleMembershipIds,
  tagFilterState,
  tags,
}: {
  filters: ReturnType<typeof useDriverFilters>;
  groups: GroupOption[];
  tagValueMap: Map<string, Record<string, string[]>>;
  visibleMembershipIds: Set<string>;
  tagFilterState: TagFilterState;
  tags: TagDefinition[];
}) {
  const isFilterActive = (key: string): boolean => {
    if (key.startsWith("tag:")) {
      const tagId = key.slice(4);
      return (tagFilterState.tagFilters[tagId]?.length ?? 0) > 0;
    }
    switch (key) {
      case "group":
        return filters.groupIds.length > 0;
      case "status":
        return filters.statusFilter.length > 0;
      case "volume":
        return filters.volumeFilter.length > 0;
      case "dutyStatus":
        return filters.dutyStatusFilter.length > 0;
      case "location":
        return filters.location !== null;
      default:
        return false;
    }
  };

  const getColumnExtra = (key: string): React.ReactNode => {
    if (key.startsWith("tag:")) {
      const tagId = key.slice(4);
      const uniqueValues = uniqueTagValuesForVisibleDrivers(tagId, tagValueMap, visibleMembershipIds);
      const activeValues = tagFilterState.tagFilters[tagId] ?? [];
      const currentSort = tagFilterState.columnSort?.columnKey === tagId ? tagFilterState.columnSort.direction : null;
      const tagDef = tags.find((t) => t.id === tagId);
      return (
        <TagColumnDropdown
          tagId={tagId}
          tagLabel={tagDef?.label ?? tagId}
          uniqueValues={uniqueValues}
          activeValues={activeValues}
          toggleValue={(v) => tagFilterState.toggleTagFilterValue(tagId, v)}
          clearFilter={() => tagFilterState.clearTagFilter(tagId)}
          selectAllVisible={(values) => tagFilterState.selectTagFilterValues(tagId, values)}
          isActive={isFilterActive(key)}
          sortDirection={currentSort}
          onSort={(direction) => tagFilterState.setColumnSort(direction ? { columnKey: tagId, direction } : null)}
        />
      );
    }
    switch (key) {
      case "driver":
        return <SortDropdown sort={filters.sort} setSort={filters.setSort} />;
      case "group":
        return (
          <GroupFilterDropdown
            groups={groups}
            toggleGroupId={filters.toggleGroupId}
            selectGroupIds={filters.selectGroupIds}
            clearGroupIds={filters.clearGroupIds}
            isActive={isFilterActive("group")}
            sortDirection={
              tagFilterState.columnSort?.columnKey === GROUP_SORT_COLUMN_KEY
                ? tagFilterState.columnSort.direction
                : null
            }
            onSort={(direction) =>
              tagFilterState.setColumnSort(direction ? { columnKey: GROUP_SORT_COLUMN_KEY, direction } : null)
            }
          />
        );
      case "status":
        return (
          <StatusFilterDropdown
            statusFilter={filters.statusFilter}
            toggleStatusFilter={filters.toggleStatusFilter}
            clearStatusFilter={filters.clearStatusFilter}
            isActive={isFilterActive("status")}
          />
        );
      case "volume":
        return (
          <VolumeFilterDropdown
            volumeFilter={filters.volumeFilter}
            toggleVolumeFilter={filters.toggleVolumeFilter}
            clearVolumeFilter={filters.clearVolumeFilter}
            isActive={isFilterActive("volume")}
          />
        );
      case "dutyStatus":
        return (
          <DutyStatusFilterDropdown
            dutyStatusFilter={filters.dutyStatusFilter}
            toggleDutyStatusFilter={filters.toggleDutyStatusFilter}
            clearDutyStatusFilter={filters.clearDutyStatusFilter}
            isActive={isFilterActive("dutyStatus")}
          />
        );
      case "location":
        return (
          <LocationFilterDropdown
            location={filters.location}
            setLocation={filters.setLocation}
            clearLocation={filters.clearLocation}
            isActive={isFilterActive("location")}
          />
        );
      default:
        return null;
    }
  };

  const getActiveFilterValues = (key: string): string[] => {
    if (key.startsWith("tag:")) {
      return tagFilterState.tagFilters[key.slice(4)] ?? [];
    }
    return [];
  };

  return {
    getColumnExtra,
    isFilterActive,
    getActiveFilterValues,
    showUnreadOnly: filters.showUnreadOnly,
    toggleUnreadOnly: filters.toggleUnreadOnly,
  };
}

const SORT_OPTIONS: {
  text: string;
  value: DriverSortKey;
  icon: React.ReactNode;
}[] = [
  {
    text: "Alphabetically ASC",
    value: "alphaAsc",
    icon: <AlphaSort width={15} />,
  },
  {
    text: "Alphabetically DESC",
    value: "alphaDesc",
    icon: <AlphaSortDesc width={15} />,
  },
  {
    text: "Most Recent",
    value: "most-recent",
    icon: <RecentSort width={15} />,
  },
  {
    text: "Unread",
    value: "unread",
    icon: <MarkUnreadChatAltIcon sx={{ fontSize: "15px" }} />,
  },
];

function SortDropdown({ sort, setSort }: { sort: DriverSortKey; setSort: (v: DriverSortKey) => void }) {
  return (
    <HeaderPopover icon={<SwapVertIcon sx={{ fontSize: 18 }} />}>
      <Box sx={{ p: 0.5 }}>
        <FilterSectionHeader title="Sort Drivers" />
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {SORT_OPTIONS.map((option) => (
            <FilterListItem
              key={option.value}
              testId={TestIds.driverView.sortOption}
              checked={option.value === sort}
              onClick={() => {
                setSort(option.value);
                ampli.updateSort({ columnName: option.value });
              }}
              label={option.text}
              active={option.value === sort}
              showCheckbox={false}
              icon={option.icon}
            />
          ))}
        </Box>
      </Box>
    </HeaderPopover>
  );
}

function GroupFilterDropdown({
  groups,
  toggleGroupId,
  selectGroupIds,
  clearGroupIds,
  isActive,
  sortDirection,
  onSort,
}: {
  groups: GroupOption[];
  toggleGroupId: (id: string) => void;
  selectGroupIds: (ids: string[]) => void;
  clearGroupIds: () => void;
  isActive: boolean;
  sortDirection: "asc" | "desc" | null;
  onSort: (direction: "asc" | "desc" | null) => void;
}) {
  // The list itself follows the direction — pinned by the roster-filters e2e — while the shared
  // `columnSort` additionally drives the table, which is what this control previously failed to do.
  const uniqueValues = useMemo(() => {
    const names = groups.map((g) => g.name);
    if (sortDirection === "desc") return names.sort((a, b) => b.localeCompare(a));
    return names.sort((a, b) => a.localeCompare(b));
  }, [groups, sortDirection]);
  const activeValues = useMemo(() => groups.filter((g) => g.active).map((g) => g.name), [groups]);
  const groupByName = useMemo(() => new Map(groups.map((g) => [g.name, g])), [groups]);

  const trackingToggle = (name: string) => {
    const g = groupByName.get(name);
    if (g) toggleGroupId(g.id);
    ampli.filterColumn({ columnName: "group" });
  };

  const trackingClear = () => {
    clearGroupIds();
    ampli.filterColumn({ columnName: "group" });
  };

  const trackingSelectAll = (names: string[]) => {
    const ids = names.map((n) => groupByName.get(n)?.id).filter((id): id is string => id != null);
    selectGroupIds(ids);
    ampli.filterColumn({ columnName: "group" });
  };

  return (
    <HeaderPopover
      icon={<FilterListIcon sx={{ fontSize: 18 }} />}
      isActive={isActive || sortDirection !== null}
      maxHeight="calc(100vh / 1.8)"
    >
      <Box sx={{ p: 0.5, width: 260 }}>
        <TagSortSection columnKey={GROUP_SORT_COLUMN_KEY} sortDirection={sortDirection} onSort={onSort} />
        <SearchableTagFilter
          tagId="group"
          uniqueValues={uniqueValues}
          activeValues={activeValues}
          toggleValue={trackingToggle}
          clearFilter={trackingClear}
          selectAllVisible={trackingSelectAll}
        />
      </Box>
    </HeaderPopover>
  );
}

/**
 * Shared states borrow the cell's colors so the rows, the header legend, and this dropdown agree.
 */
const STATUS_DOT_COLORS: Record<string, string> = {
  driving: HANDS_FREE_DISPLAYS.available.color,
  "not-driving": HANDS_FREE_DISPLAYS.handsFreeOn.color,
  "hands-free-off": HANDS_FREE_DISPLAYS.handsFreeOff.color,
  "volume-muted": HANDS_FREE_DISPLAYS.deviceMuted.color,
  offline: OFFLINE_DISPLAY.color,
  "never-logged-in": NEVER_LOGGED_IN_DISPLAY.color,
};

type StatusFilterOption = {
  text: string;
  value: DriverStatusFilter | null;
};

/**
 * Groups mirror the mockup: hands-free buckets describe a work device, the personal-device indicator is
 * its own signal, and never-logged-in stands apart from both. `No Filter` leads the list ungrouped.
 */
const STATUS_FILTER_GROUPS: { heading: string | null; options: StatusFilterOption[] }[] = [
  { heading: null, options: [{ text: "No Filter", value: null }] },
  {
    heading: "Work device",
    options: [
      { text: "Available", value: "driving" },
      { text: "Hands-free ON", value: "not-driving" },
      { text: "Hands-free OFF", value: "hands-free-off" },
      { text: "Device muted", value: "volume-muted" },
      { text: "Offline", value: "offline" },
    ],
  },
  { heading: "Personal device", options: [{ text: "On a personal device", value: "personal-device" }] },
  { heading: "Never logged in", options: [{ text: "Never Logged In", value: "never-logged-in" }] },
];

function StatusDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

/** Lighter than `FilterSectionHeader`, which already titles the popover and would crowd it if repeated. */
function FilterGroupHeading({ title }: { title: string }) {
  return (
    <Box sx={{ px: 1, pt: 0.75, pb: 0.25, fontSize: "0.6875rem", fontWeight: 600, color: zinc[500] }}>{title}</Box>
  );
}

function StatusFilterDropdown({
  statusFilter,
  toggleStatusFilter,
  clearStatusFilter,
  isActive: filterIsActive,
}: {
  statusFilter: DriverStatusFilter[];
  toggleStatusFilter: (v: DriverStatusFilter) => void;
  clearStatusFilter: () => void;
  isActive: boolean;
}) {
  const neverLoggedInOnly = statusFilter.length === 1 && statusFilter[0] === "never-logged-in";

  const renderOption = (option: StatusFilterOption) => {
    const isActive = option.value === null ? statusFilter.length === 0 : statusFilter.includes(option.value);
    const optionDisabled = neverLoggedInOnly && option.value !== null && option.value !== "never-logged-in";
    return (
      <FilterListItem
        key={`filter-status-${option.text}`}
        checked={isActive}
        disabled={optionDisabled}
        onClick={() => {
          if (option.value === null) {
            clearStatusFilter();
          } else {
            toggleStatusFilter(option.value);
          }
          ampli.filterColumn({ columnName: "status" });
        }}
        label={option.text}
        active={isActive}
        startAdornment={
          option.value === "personal-device" ? (
            <SmartphoneIcon sx={{ fontSize: 14, color: isActive ? "inherit" : zinc[600] }} />
          ) : (
            <StatusDot color={isActive ? (option.value ? STATUS_DOT_COLORS[option.value] : zinc[400]) : zinc[600]} />
          )
        }
      />
    );
  };

  return (
    <HeaderPopover icon={<FilterListIcon sx={{ fontSize: 18 }} />} isActive={filterIsActive}>
      <Box sx={{ p: 0.5 }}>
        <FilterSectionHeader title="Filter by Driver Availability" />
        {STATUS_FILTER_GROUPS.map((group) => (
          <Box key={group.heading ?? "ungrouped"}>
            {group.heading && <FilterGroupHeading title={group.heading} />}
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {group.options.map(renderOption)}
            </Box>
          </Box>
        ))}
      </Box>
    </HeaderPopover>
  );
}

const VOLUME_FILTER_OPTIONS: {
  text: string;
  value: DriverVolumeFilter | null;
}[] = [
  { text: "No Filter", value: null },
  { text: "100%", value: "100" },
  { text: "75% – 99%", value: "75-99" },
  { text: "50% – 74%", value: "50-74" },
  { text: "25% – 49%", value: "25-49" },
  { text: "1% – 24%", value: "1-24" },
  { text: "Devices muted", value: "muted" },
];

function VolumeFilterDropdown({
  volumeFilter,
  toggleVolumeFilter,
  clearVolumeFilter,
  isActive: filterIsActive,
}: {
  volumeFilter: DriverVolumeFilter[];
  toggleVolumeFilter: (v: DriverVolumeFilter) => void;
  clearVolumeFilter: () => void;
  isActive: boolean;
}) {
  return (
    <HeaderPopover icon={<FilterListIcon sx={{ fontSize: 18 }} />} isActive={filterIsActive}>
      <Box sx={{ p: 0.5 }}>
        <FilterSectionHeader title="Filter by Device Volume" />
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {VOLUME_FILTER_OPTIONS.map((option) => {
            const isActive = option.value === null ? volumeFilter.length === 0 : volumeFilter.includes(option.value);
            return (
              <FilterListItem
                key={`filter-volume-${option.text}`}
                checked={isActive}
                onClick={() => {
                  if (option.value === null) {
                    clearVolumeFilter();
                  } else {
                    toggleVolumeFilter(option.value);
                  }
                  ampli.filterColumn({ columnName: "volume" });
                }}
                label={option.text}
                active={isActive}
              />
            );
          })}
        </Box>
      </Box>
    </HeaderPopover>
  );
}

function DutyStatusFilterDropdown({
  dutyStatusFilter,
  toggleDutyStatusFilter,
  clearDutyStatusFilter,
  isActive: filterIsActive,
}: {
  dutyStatusFilter: DriverDutyStatusFilter[];
  toggleDutyStatusFilter: (v: DriverDutyStatusFilter) => void;
  clearDutyStatusFilter: () => void;
  isActive: boolean;
}) {
  return (
    <HeaderPopover icon={<FilterListIcon sx={{ fontSize: 18 }} />} isActive={filterIsActive}>
      <Box sx={{ p: 0.5 }}>
        <FilterSectionHeader title="Filter by Duty Status" />
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          <FilterListItem
            key="filter-duty-status-none"
            checked={dutyStatusFilter.length === 0}
            onClick={() => {
              clearDutyStatusFilter();
              ampli.filterColumn({ columnName: "dutyStatus" });
            }}
            label="No Filter"
            active={dutyStatusFilter.length === 0}
          />
          {DRIVER_DUTY_STATUS_FILTERS.map((value) => {
            const checked = dutyStatusFilter.includes(value);
            return (
              <FilterListItem
                key={`filter-duty-status-${value}`}
                checked={checked}
                onClick={() => {
                  toggleDutyStatusFilter(value);
                  ampli.filterColumn({ columnName: "dutyStatus" });
                }}
                label={formatDutyStatusLabel(value)}
                active={checked}
              />
            );
          })}
        </Box>
      </Box>
    </HeaderPopover>
  );
}

const SEARCHABLE_THRESHOLD = 10;

function TagSortSection({
  columnKey,
  sortDirection,
  onSort,
}: {
  columnKey: string;
  sortDirection: "asc" | "desc" | null;
  onSort: (direction: "asc" | "desc" | null) => void;
}) {
  return (
    <>
      <FilterSectionHeader title="Sort" />
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
        <FilterListItem
          key={`column-sort-${columnKey}-none`}
          testId={TestIds.driverView.sortOption}
          checked={sortDirection === null}
          onClick={() => onSort(null)}
          label="No Sort"
          active={sortDirection === null}
          showCheckbox={false}
        />
        <FilterListItem
          key={`column-sort-${columnKey}-asc`}
          testId={TestIds.driverView.sortOption}
          checked={sortDirection === "asc"}
          onClick={() => onSort(sortDirection === "asc" ? null : "asc")}
          label="A → Z"
          active={sortDirection === "asc"}
          showCheckbox={false}
          icon={<AlphaSort width={15} />}
        />
        <FilterListItem
          key={`column-sort-${columnKey}-desc`}
          testId={TestIds.driverView.sortOption}
          checked={sortDirection === "desc"}
          onClick={() => onSort(sortDirection === "desc" ? null : "desc")}
          label="Z → A"
          active={sortDirection === "desc"}
          showCheckbox={false}
          icon={<AlphaSortDesc width={15} />}
        />
      </Box>
      <Box sx={{ mb: 1 }} />
    </>
  );
}

function SearchableTagFilter({
  tagId,
  uniqueValues,
  activeValues,
  toggleValue,
  clearFilter,
  selectAllVisible,
}: {
  tagId: string;
  uniqueValues: string[];
  activeValues: string[];
  toggleValue: (value: string) => void;
  clearFilter: () => void;
  selectAllVisible: (values: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeSet = useMemo(() => new Set(activeValues), [activeValues]);

  // Focus the search input when the popover opens so users can type immediately.
  // The popover unmounts its children on close, so this remounts and refocuses on
  // every reopen. Defer to the next frame so we win over the MUI Popover FocusTrap,
  // which focuses the dialog container on open and would otherwise steal focus.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return uniqueValues;
    const q = search.toLowerCase();
    return uniqueValues.filter((v) => v.toLowerCase().includes(q));
  }, [uniqueValues, search]);

  const handleClearAll = () => {
    clearFilter();
    setSearch("");
  };

  const handleSelectAllVisible = () => {
    selectAllVisible(filtered);
  };

  return (
    <>
      <FilterSectionHeader title="Filter" />
      <Box
        sx={{
          position: "relative",
          mx: 0.5,
          mb: 0.75,
        }}
      >
        <SearchIcon
          sx={{
            position: "absolute",
            left: 6,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 16,
            color: zinc[400],
            pointerEvents: "none",
          }}
        />
        <InputBase
          data-sb={TestIds.driverView.filterSearch}
          inputRef={searchInputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          sx={{
            width: "100%",
            bgcolor: zinc[700],
            borderRadius: 1,
            px: 1,
            pl: 3.5,
            py: 0.25,
            fontSize: "0.8125rem",
            color: "white",
            "& .MuiInputBase-input": {
              p: 0,
              "&::placeholder": { color: zinc[400], opacity: 1 },
            },
          }}
        />
      </Box>
      <Box sx={{ mx: 0.5, mb: 0.5, display: "flex", gap: 0.5 }}>
        {search && (
          <ButtonBase
            data-sb={TestIds.driverView.filterSelectAll}
            onClick={handleSelectAllVisible}
            sx={{
              flex: 1,
              justifyContent: "center",
              fontSize: "0.75rem",
              color: zinc[400],
              py: 0.25,
              borderRadius: 1,
              "&:hover": { color: "white", bgcolor: zinc[700] },
            }}
          >
            Select All
          </ButtonBase>
        )}
        <ButtonBase
          data-sb={TestIds.driverView.filterClearAll}
          onClick={handleClearAll}
          sx={{
            flex: 1,
            justifyContent: "center",
            fontSize: "0.75rem",
            color: zinc[400],
            py: 0.25,
            borderRadius: 1,
            "&:hover": { color: "white", bgcolor: zinc[700] },
          }}
        >
          <FilterAltOffIcon sx={{ fontSize: 14, mr: 0.5 }} />
          Clear All
        </ButtonBase>
      </Box>
      <Box
        component="ul"
        sx={{
          listStyle: "none",
          p: 0,
          m: 0,
          maxHeight: 240,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: zinc[600], borderRadius: 2 },
        }}
      >
        {filtered.map((value) => {
          const isChecked = activeSet.has(value);
          return (
            <Box
              component="li"
              key={`tag-filter-${tagId}-${value}`}
              sx={{
                "&:not(:last-child)": { mb: 0.25 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <ButtonBase
                data-sb={TestIds.driverView.filterOption}
                onClick={() => toggleValue(value)}
                sx={{
                  width: "100%",
                  px: 1,
                  py: 0.25,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  borderRadius: 1,
                  justifyContent: "flex-start",
                  color: isChecked ? "white" : zinc[400],
                  bgcolor: isChecked ? zinc[700] : "transparent",
                  "&:hover": { bgcolor: zinc[700] },
                  fontSize: "0.8125rem",
                }}
              >
                <Checkbox sx={{ p: 0 }} checked={isChecked} size="small" />
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value}
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
        {filtered.length === 0 && (
          <Box
            sx={{
              px: 1,
              py: 1,
              fontSize: "0.8125rem",
              color: zinc[500],
              textAlign: "center",
            }}
          >
            No matches
          </Box>
        )}
      </Box>
    </>
  );
}

function SimpleTagFilter({
  tagId,
  uniqueValues,
  activeValues,
  toggleValue,
  clearFilter,
}: {
  tagId: string;
  uniqueValues: string[];
  activeValues: string[];
  toggleValue: (value: string) => void;
  clearFilter: () => void;
}) {
  return (
    <>
      <FilterSectionHeader title="Filter" />
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
        <FilterListItem
          key={`tag-filter-${tagId}-clear`}
          checked={activeValues.length === 0}
          onClick={clearFilter}
          label="No Filter"
          active={activeValues.length === 0}
        />
        {uniqueValues.map((value) => (
          <FilterListItem
            key={`tag-filter-${tagId}-${value}`}
            checked={activeValues.includes(value)}
            onClick={() => toggleValue(value)}
            label={value}
            active={activeValues.includes(value)}
          />
        ))}
      </Box>
    </>
  );
}

function TagColumnDropdown({
  tagId,
  tagLabel,
  uniqueValues,
  activeValues,
  toggleValue,
  clearFilter,
  selectAllVisible,
  isActive,
  sortDirection,
  onSort,
}: {
  tagId: string;
  tagLabel: string;
  uniqueValues: string[];
  activeValues: string[];
  toggleValue: (value: string) => void;
  clearFilter: () => void;
  selectAllVisible: (values: string[]) => void;
  isActive: boolean;
  sortDirection: "asc" | "desc" | null;
  onSort: (direction: "asc" | "desc" | null) => void;
}) {
  const useSearchable = uniqueValues.length > SEARCHABLE_THRESHOLD;

  const trackingToggle = (value: string) => {
    toggleValue(value);
    ampli.filterColumn({ columnName: tagLabel });
  };

  const trackingClear = () => {
    clearFilter();
    ampli.filterColumn({ columnName: tagLabel });
  };

  const trackingSelectAll = (values: string[]) => {
    selectAllVisible(values);
    ampli.filterColumn({ columnName: tagLabel });
  };

  return (
    <HeaderPopover
      icon={<FilterListIcon sx={{ fontSize: 18 }} />}
      isActive={isActive || sortDirection !== null}
      maxHeight={useSearchable ? "calc(100vh / 1.8)" : undefined}
    >
      <Box sx={{ p: 0.5, width: useSearchable ? 260 : undefined }}>
        <TagSortSection columnKey={tagId} sortDirection={sortDirection} onSort={onSort} />
        {useSearchable ? (
          <SearchableTagFilter
            tagId={tagId}
            uniqueValues={uniqueValues}
            activeValues={activeValues}
            toggleValue={trackingToggle}
            clearFilter={trackingClear}
            selectAllVisible={trackingSelectAll}
          />
        ) : (
          <SimpleTagFilter
            tagId={tagId}
            uniqueValues={uniqueValues}
            activeValues={activeValues}
            toggleValue={trackingToggle}
            clearFilter={trackingClear}
          />
        )}
      </Box>
    </HeaderPopover>
  );
}

function LocationFilterDropdown({
  location,
  setLocation,
  clearLocation,
  isActive,
}: {
  location: LocationFilter | null;
  setLocation: (v: LocationFilter) => void;
  clearLocation: () => void;
  isActive: boolean;
}) {
  const form = useLocationFilterForm({
    location,
    setLocation: (v: LocationFilter) => {
      setLocation(v);
      ampli.filterColumn({ columnName: "location" });
    },
    clearLocation: () => {
      clearLocation();
      ampli.filterColumn({ columnName: "location" });
    },
  });

  return (
    <HeaderPopover icon={<FilterListIcon sx={{ fontSize: 18 }} />} isActive={isActive} maxHeight="calc(100vh / 1.5)">
      <Box sx={{ p: 0.5, width: 400 }}>
        <FilterSectionHeader title="Filter by Location" />

        <SegmentedControl
          testId={TestIds.driverView.locationOption}
          options={[
            { label: "Address", value: "address" as const },
            { label: "Coordinates", value: "coordinates" as const },
          ]}
          value={form.searchType}
          onChange={(v) => form.setSearchType(v)}
        />

        {form.searchType === "address" ? (
          <Box sx={{ width: "100%", my: 1 }}>
            <GooglePlacesAutocomplete
              location={form.addressLabel}
              resetForm={form.resetForm}
              setLocationValue={(value) => form.handlePlaceSelected(value)}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1, my: 1, width: "100%" }}>
            <Box sx={{ width: "100%" }} data-sb={TestIds.driverView.locationLatitude}>
              <TextField
                onChange={(e) => form.setLatitude(e.target.value)}
                value={form.latitude}
                placeholder="Latitude"
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(82, 82, 91, 0.5)",
                    borderRadius: 1,
                    "& fieldset": { borderColor: "var(--color-zinc-500)" },
                  },
                }}
              />
            </Box>
            <Box sx={{ width: "100%" }} data-sb={TestIds.driverView.locationLongitude}>
              <TextField
                onChange={(e) => form.setLongitude(e.target.value)}
                value={form.longitude}
                placeholder="Longitude"
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(82, 82, 91, 0.5)",
                    borderRadius: 1,
                    "& fieldset": { borderColor: "var(--color-zinc-500)" },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {form.isValid && (
          <Box sx={{ fontSize: "0.75rem", color: zinc[400], mb: 1, px: 0.5 }}>
            Coordinates: {form.latNum.toFixed(4)}, {form.lonNum.toFixed(4)}
          </Box>
        )}

        <Box>
          <Box>
            Radius: {form.range} {form.distanceUnit}
          </Box>
          <Box sx={{ px: 1 }}>
            <Slider
              max={500}
              size="small"
              sx={(theme) => ({
                flexGrow: 1,
                color: "red",
                "& .MuiSlider-thumb": { color: "white", width: 12, height: 12 },
                "& .MuiSlider-track": {
                  color: theme.palette.brandPrimary[500],
                  height: 4,
                },
                "& .MuiSlider-rail": {
                  color: zinc[500],
                  height: 4,
                  opacity: 0.3,
                },
              })}
              onChange={(_e, value) => form.setRange(value as number)}
              aria-label="Location radius"
              defaultValue={30}
              value={form.range}
            />
          </Box>
        </Box>

        <Box>
          <Box>Distance Unit</Box>
          <SegmentedControl
            testId={TestIds.driverView.locationOption}
            options={[
              { label: "Miles", value: "miles" as const },
              { label: "Kilometers", value: "km" as const },
            ]}
            value={form.distanceUnit}
            onChange={(v) => form.setDistanceUnit(v)}
          />
        </Box>

        <Box sx={{ mt: 1, mb: 0.5, display: "flex", gap: 1 }}>
          <ButtonBase
            data-sb={TestIds.driverView.locationApply}
            disabled={!form.isValid}
            onClick={form.apply}
            sx={(theme) => ({
              width: "100%",
              justifyContent: "center",
              border: "1px solid",
              borderColor: theme.palette.brandPrimary[500],
              p: 1,
              borderRadius: 1,
              fontSize: "0.875rem",
              bgcolor: theme.palette.brandPrimary[500],
              color: "white",
              "&:hover": { bgcolor: theme.palette.brandPrimary[600] },
              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            })}
          >
            Apply Filter
          </ButtonBase>
          <ButtonBase
            data-sb={TestIds.driverView.locationClear}
            disabled={!form.canClear}
            onClick={form.reset}
            sx={{
              width: "100%",
              justifyContent: "center",
              color: zinc[400],
              border: "1px solid",
              borderColor: zinc[500],
              p: 1,
              borderRadius: 1,
              fontSize: "0.875rem",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { bgcolor: zinc[700] },
              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            }}
          >
            <FilterAltOffIcon sx={{ fontSize: "15px" }} />
            Clear
          </ButtonBase>
        </Box>
      </Box>
    </HeaderPopover>
  );
}
